import React, { useState, useLayoutEffect, useRef, useMemo } from 'react'
import { useCrosswordStore } from '../store'
import { getLetters, getLettersFromText, passesUsedFilter } from '../lib/pairings'
import { PairingListItem } from './ListItems'
import { TabGroup } from './TabGroup'
import { SearchFilterBar } from './SearchFilterBar'
import { FragmentsPanel } from './constructor/FragmentsPanel'
import { DraftSelector } from './constructor/DraftSelector'
import { useSearch } from '../contexts/SearchContext'
import { usePlacedWordTexts, isWordPlaced } from '../hooks/usePlacedWordTexts'
import { passesTagFilter, type TagFilterState } from './TagFilterRow'
import { passesRatingFilter, type RatingFilterState } from './RatingFilter'
import type { Word, Pairing, PairingStatus, ClueStatus, FittingPair } from '../types'

const COLUMN_TABS = ['pairs', 'fragments', 'drafts'] as const
const EMPTY_TAG_SET: ReadonlySet<string> = new Set()

export type ColumnTab = typeof COLUMN_TABS[number]

type ExploreColumnProps = {
  onPairDragStart?: ((pairingId: string, e: React.PointerEvent) => void) | undefined
  onPairClick?: ((pairingId: string) => void) | undefined
  selectedGridCell?: { row: number; col: number } | null | undefined
  fittingPairs?: { across: FittingPair[]; down: FittingPair[] } | undefined
  onPlaceFittingPair?: ((pair: FittingPair, direction: 'across' | 'down') => void) | undefined
  onPreviewFittingPair?: ((pair: FittingPair | null, direction: 'across' | 'down') => void) | undefined
  onPairHover?: ((wordTexts: string[] | null) => void) | undefined
  activeTab?: ColumnTab | undefined
  onActiveTabChange?: ((tab: ColumnTab) => void) | undefined
  // Lifted filter state — when provided, ExploreColumn becomes a controlled
  // consumer so siblings (e.g. PairDetailColumn / CrossingsPanel) can also
  // read the same filter values.
  statusFilters?: ReadonlyArray<PairingStatus> | undefined
  onStatusFiltersChange?: ((statuses: PairingStatus[]) => void) | undefined
  clueFilters?: ReadonlyArray<ClueStatus | 'empty'> | undefined
  onClueFiltersChange?: ((statuses: (ClueStatus | 'empty')[]) => void) | undefined
  tagFilters?: TagFilterState | undefined
  usedTagsInGrid?: ReadonlySet<string> | undefined
  // Used/unused filter — derived axis, scope decided by parent
  // (placedPairingIds = whatever counts as "placed" right now).
  usedFilter?: 'all' | 'used' | 'unused' | undefined
  placedPairingIds?: ReadonlySet<string> | undefined
  // Rating filter (1–5 with ≥ / = / ≤). Pairs without a rating fail '=' and
  // pass '>=' / '<=' only when threshold is null.
  ratingFilter?: RatingFilterState | undefined
}

const DRAG_THRESHOLD = 5 // pixels of movement to trigger drag vs click

export function ExploreColumn({
  onPairDragStart,
  onPairClick,
  selectedGridCell,
  fittingPairs,
  onPlaceFittingPair,
  onPreviewFittingPair,
  onPairHover,
  activeTab: activeTabProp,
  onActiveTabChange,
  statusFilters: statusFiltersProp,
  onStatusFiltersChange: _onStatusFiltersChange,
  clueFilters: clueFiltersProp,
  onClueFiltersChange: _onClueFiltersChange,
  tagFilters,
  usedTagsInGrid,
  usedFilter,
  placedPairingIds,
  ratingFilter,
}: ExploreColumnProps = {}) {
  // Render-cycle timing. Logs synchronous render duration (start of function body
  // to layout-effect commit) and the full mount-to-paint window. Reports counts
  // alongside so a slow log line is self-explanatory.
  const renderStartRef = useRef(performance.now())
  renderStartRef.current = performance.now()
  const isFirstRenderRef = useRef(true)
  useLayoutEffect(() => {
    const renderMs = performance.now() - renderStartRef.current
    if (isFirstRenderRef.current) {
      console.log(`[perf] ExploreColumn first render: ${renderMs.toFixed(1)}ms`)
      isFirstRenderRef.current = false
      // Log paint time after the browser actually paints.
      const mountStart = renderStartRef.current
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          console.log(`[perf] ExploreColumn mount → paint: ${(performance.now() - mountStart).toFixed(1)}ms`)
        })
      })
    } else if (renderMs > 16) {
      console.log(`[perf] ExploreColumn re-render: ${renderMs.toFixed(1)}ms`)
    }
  })

  const {
    words,
    pairings,
    selectedPairingId,
    setSelectedPairing,
    getOrCreatePairing,
    updatePairingStatus,
  } = useCrosswordStore()

  const searchContext = useSearch()
  const letterSearch = searchContext?.letterSearch ?? []
  const searchPattern = searchContext?.searchPattern ?? ''
  const placedWordTexts = usePlacedWordTexts()

  // Local UI state
  const [activeTabLocal, setActiveTabLocal] = useState<ColumnTab>('pairs')
  const activeTab = activeTabProp ?? activeTabLocal
  const setActiveTab = (tab: ColumnTab) => {
    if (onActiveTabChange) onActiveTabChange(tab)
    else setActiveTabLocal(tab)
  }

  // Filters: controlled when parent supplies them, otherwise local state. The
  // controlled path lets siblings (PairDetailColumn / CrossingsPanel) react to
  // the same filters the user sees in this column.
  const [localStatusFilters] = useState<PairingStatus[]>([])
  const [localClueFilters] = useState<(ClueStatus | 'empty')[]>([])
  const statusFilters: ReadonlyArray<PairingStatus> = statusFiltersProp ?? localStatusFilters
  const clueFilters: ReadonlyArray<ClueStatus | 'empty'> = clueFiltersProp ?? localClueFilters




  const pairingsById = useMemo(
    () => new Map(pairings.map(p => [p.id, p])),
    [pairings]
  )

  const wordsById = useMemo(
    () => new Map(words.map(word => [word.id, word])),
    [words]
  )


  const hasSearch = searchContext?.hasSearch ?? letterSearch.some(l => l.trim())

  // Compute search match helpers
  const upperLetterSearch = letterSearch.map(l => l.toUpperCase())
  const filledSearchIndices = letterSearch.map((l, i) => l.trim() ? i : -1).filter(i => i >= 0)
  const searchLetters = letterSearch.filter(l => l.trim()).join('').toUpperCase()
  const isPrefixSearch = filledSearchIndices.every((idx, i) => idx === i)

  const matchesSearch = (text: string): boolean => {
    if (!hasSearch) return true

    const letters = getLettersFromText(text)

    if (isPrefixSearch) {
      return letters.startsWith(searchLetters)
    } else {
      return filledSearchIndices.every(idx => letters[idx] === upperLetterSearch[idx])
    }
  }

  const matchesPairSearch = (textA: string, textB: string): boolean => {
    if (!hasSearch) return true
    return matchesSearch(textA) ||
      matchesSearch(textB) ||
      (searchContext?.getVerticalMatchIndices(textA, textB).length ?? 0) > 0
  }



  // Build set of fitting pair IDs for quick lookup (real pairings only)
  const fittingPairIds = new Set<string>()
  // Also build a map for quick FittingPair lookup
  const fittingPairMap = new Map<string, { pair: FittingPair; direction: 'across' | 'down' }>()
  if (fittingPairs) {
    for (const pair of fittingPairs.across) {
      if (pair.pairingId) {
        fittingPairIds.add(pair.pairingId)
        fittingPairMap.set(pair.pairingId, { pair, direction: 'across' })
      }
    }
    for (const pair of fittingPairs.down) {
      if (pair.pairingId) {
        fittingPairIds.add(pair.pairingId)
        fittingPairMap.set(pair.pairingId, { pair, direction: 'down' })
      }
    }
  }

  // Get the FittingPair and direction for a pairing
  const getFittingPairInfo = (pairing: Pairing): { pair: FittingPair; direction: 'across' | 'down' } | null => {
    return fittingPairMap.get(pairing.id) ?? null
  }


  // Reuse the matchesPairSearch / matchesSearch helpers defined at outer
  // render scope (they wrap searchContext, which knows about both horizontal
  // letter slots and vertical digraph matching).
  const hasSearchForPairings = hasSearch

  // Filter recorded pairings. Default hides rejected unless explicitly chipped.
  let pairingResult = hasSearchForPairings
    ? pairings.filter(p => {
        const wordA = wordsById.get(p.wordAId)
        const wordB = wordsById.get(p.wordBId)
        if (!wordA || !wordB) return false
        return matchesPairSearch(wordA.text, wordB.text)
      })
    : pairings
  pairingResult = pairingResult.filter(p => {
    if (statusFilters.length === 0) return p.status !== 'rejected'
    return statusFilters.includes(p.status)
  })
  if (clueFilters.length > 0) {
    pairingResult = pairingResult.filter(p => {
      const clueStatus = p.clue?.text ? p.clue.status : 'empty'
      return clueFilters.includes(clueStatus)
    })
  }
  // Tag filters apply to recorded pairings; unrecorded pairs are handled
  // below (they have no tags so include filters always exclude them, etc.).
  if (tagFilters) {
    const used = usedTagsInGrid ?? EMPTY_TAG_SET
    pairingResult = pairingResult.filter(p => passesTagFilter(p.tags, tagFilters, used))
  }
  // Used / unused filter — derived from placedPairingIds.
  if (usedFilter && usedFilter !== 'all' && placedPairingIds) {
    pairingResult = pairingResult.filter(p => passesUsedFilter(p.id, usedFilter, placedPairingIds))
  }
  // Rating filter — independent quality axis.
  if (ratingFilter && ratingFilter.threshold !== null) {
    pairingResult = pairingResult.filter(p => passesRatingFilter(p.rating, ratingFilter))
  }
  const filteredPairings = [...pairingResult].sort((a, b) => {
    if (selectedGridCell && fittingPairs) {
      const aFits = fittingPairIds.has(a.id) ? 0 : 1
      const bFits = fittingPairIds.has(b.id) ? 0 : 1
      if (aFits !== bFits) return aFits - bFits
    }
    const aRejected = a.status === 'rejected' ? 1 : 0
    const bRejected = b.status === 'rejected' ? 1 : 0
    return aRejected - bRejected
  })

  // Unrecorded combos: every compatible-length (A, B) pair without a metadata
  // record. Only generated when the user is searching — without a search this
  // is hundreds × hundreds of items. Filter chips treat unrecorded as
  // status='none' / clue='empty'.
  const unrecordedMatches: Array<{ wordA: Word; wordB: Word }> = []
  const unrecordedPasses =
    (statusFilters.length === 0 || statusFilters.includes('none')) &&
    (clueFilters.length === 0 || clueFilters.includes('empty')) &&
    // Unrecorded pairs have no tags, so an include-tag filter excludes them.
    (!tagFilters || tagFilters.include.length === 0) &&
    // Unrecorded pairs are by definition unused — hide them when filtering for "used".
    usedFilter !== 'used' &&
    // Unrecorded pairs have no rating — any active rating filter excludes them.
    (!ratingFilter || ratingFilter.threshold === null)
  if (hasSearchForPairings && unrecordedPasses) {
    const recordedKey = (a: string, b: string) => a < b ? `${a}:${b}` : `${b}:${a}`
    const recordedKeys = new Set<string>()
    for (const p of pairings) recordedKeys.add(recordedKey(p.wordAId, p.wordBId))

    const aWords = words.filter(w => w.list === 'A')
    const bWordsByLen = new Map<number, Word[]>()
    for (const w of words) {
      if (w.list !== 'B') continue
      const len = getLetters(w).length
      const bucket = bWordsByLen.get(len)
      if (bucket) bucket.push(w)
      else bWordsByLen.set(len, [w])
    }

    for (const wordA of aWords) {
      const lettersA = getLetters(wordA)
      const bucket = bWordsByLen.get(lettersA.length)
      if (!bucket) continue
      for (const wordB of bucket) {
        if (recordedKeys.has(recordedKey(wordA.id, wordB.id))) continue
        if (!matchesPairSearch(wordA.text, wordB.text)) continue
        unrecordedMatches.push({ wordA, wordB })
      }
    }
    unrecordedMatches.sort((a, b) => {
      const la = getLetters(a.wordA).length
      const lb = getLetters(b.wordA).length
      if (la !== lb) return la - lb
      const aDiff = a.wordA.text.localeCompare(b.wordA.text)
      if (aDiff !== 0) return aDiff
      return a.wordB.text.localeCompare(b.wordB.text)
    })
  }


  const handleSelectPairing = (pairing: Pairing) => {
    setSelectedPairing(pairing.id)
    onPairClick?.(pairing.id)
  }

  // Track pointer for distinguishing click vs drag on pairs
  const pairPointerRef = React.useRef<{
    pairingId: string
    startX: number
    startY: number
    isDragging: boolean
  } | null>(null)


  const handlePairPointerDown = (pairingId: string, e: React.PointerEvent) => {
    if (e.button !== 0) return
    e.preventDefault()

    pairPointerRef.current = {
      pairingId,
      startX: e.clientX,
      startY: e.clientY,
      isDragging: false
    }

    // Set up document listeners for move and up
    const handlePointerMove = (moveE: PointerEvent) => {
      if (!pairPointerRef.current) return

      const dx = moveE.clientX - pairPointerRef.current.startX
      const dy = moveE.clientY - pairPointerRef.current.startY
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (distance > DRAG_THRESHOLD && !pairPointerRef.current.isDragging) {
        pairPointerRef.current.isDragging = true
        // Trigger drag start
        if (onPairDragStart) {
          onPairDragStart(pairPointerRef.current.pairingId, e)
        }
        // Clean up listeners since drag handling takes over
        document.removeEventListener('pointermove', handlePointerMove)
        document.removeEventListener('pointerup', handlePointerUp)
        pairPointerRef.current = null
      }
    }

    const handlePointerUp = () => {
      if (pairPointerRef.current && !pairPointerRef.current.isDragging) {
        // It was a click, not a drag
        const pairing = pairingsById.get(pairPointerRef.current.pairingId)
        if (pairing) {
          handleSelectPairing(pairing)
        }
      }
      pairPointerRef.current = null
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
    }

    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', handlePointerUp)
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header with tabs */}
      <div className="p-2 bg-gray-50/50">
        <TabGroup
          tabs={COLUMN_TABS}
          value={activeTab}
          onChange={setActiveTab}
        />
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden px-3 pb-0">
        {activeTab === 'pairs' && (
          <div className="flex flex-col h-full -mx-3 -mb-3">
            {/* Filter UI moved to the context strip above the grid — the
                sidebar pair list still consumes the same lifted state, so
                toggling there filters this list too. */}
            {hasSearch && <SearchFilterBar pattern={searchPattern} />}

            {/* Pairs list - scrollable, hide horizontal scrollbar */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-2">
              <div className="space-y-1">
                {filteredPairings.map(pairing => {
                  const wordA = wordsById.get(pairing.wordAId)
                  const wordB = wordsById.get(pairing.wordBId)
                  if (!wordA || !wordB) return null

                  const fittingInfo = getFittingPairInfo(pairing)
                  const fits = fittingInfo !== null

                  return (
                    <div
                      key={pairing.id}
                      onPointerDown={(e) => handlePairPointerDown(pairing.id, e)}
                      onMouseEnter={() => {
                        if (fittingInfo) onPreviewFittingPair?.(fittingInfo.pair, fittingInfo.direction)
                        onPairHover?.([wordA.text, wordB.text])
                      }}
                      onMouseLeave={() => {
                        if (fittingInfo) onPreviewFittingPair?.(null, fittingInfo.direction)
                        onPairHover?.(null)
                      }}
                      onClick={() => {
                        if (fittingInfo) {
                          onPlaceFittingPair?.(fittingInfo.pair, fittingInfo.direction)
                        }
                      }}
                      className={fits ? 'ring-2 ring-blue-300 rounded' : ''}
                      style={{ cursor: onPairDragStart ? 'grab' : 'pointer' }}
                    >
                      <PairingListItem
                        textA={wordA.text}
                        textB={wordB.text}
                        autoHighlight
                        selected={selectedPairingId === pairing.id}
                        isPlacedA={isWordPlaced(placedWordTexts, wordA.text)}
                        isPlacedB={isWordPlaced(placedWordTexts, wordB.text)}
                        pairingStatus={pairing.status}
                        clueStatus={pairing.clue?.text ? pairing.clue.status : undefined}
                        clueText={pairing.clue?.text}
                        onPairingStatusChange={(status) => updatePairingStatus(pairing.id, status)}
                      />
                    </div>
                  )
                })}
                {filteredPairings.length === 0 && unrecordedMatches.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-6">
                    No pairings match filters
                  </p>
                )}

                {/* Unrecorded combos surface only when searching (see filter
                    effect). Visually muted; clicking promotes to a real
                    Pairing record via getOrCreatePairing. */}
                {unrecordedMatches.length > 0 && (
                  <>
                    {filteredPairings.length > 0 && (
                      <div className="text-[10px] uppercase tracking-wider text-gray-400 mt-3 mb-1 px-1">
                        Unrecorded ({unrecordedMatches.length})
                      </div>
                    )}
                    {unrecordedMatches.map(({ wordA, wordB }) => (
                      <div
                        key={`unrecorded:${wordA.id}:${wordB.id}`}
                        onMouseEnter={() => onPairHover?.([wordA.text, wordB.text])}
                        onMouseLeave={() => onPairHover?.(null)}
                        onClick={() => {
                          const created = getOrCreatePairing(wordA.id, wordB.id)
                          if (created) {
                            setSelectedPairing(created.id)
                            onPairClick?.(created.id)
                          }
                        }}
                        className="cursor-pointer opacity-70 hover:opacity-100 transition-opacity"
                      >
                        <PairingListItem
                          textA={wordA.text}
                          textB={wordB.text}
                          autoHighlight
                          isPlacedA={isWordPlaced(placedWordTexts, wordA.text)}
                          isPlacedB={isWordPlaced(placedWordTexts, wordB.text)}
                          pairingStatus="none"
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'fragments' && <FragmentsPanel />}
        {activeTab === 'drafts' && <DraftSelector />}

      </div>
    </div>
  )
}
